import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { apiService } from "../../api/api";
import { ROUTES } from "../../config/routes";
import {
  getOacActivityStatus,
  oacActivityTagSeverity,
  OAC_ACTIVITY_STATUS_LABEL,
  OAC_ACTIVITY_STATUS_ORDER,
  OacChannelLastUpdateRow,
} from "./oacActivityStatus";

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * OAC Audit tab: shows channel last update data for channels in the audit list.
 * Status: Compliant = uploaded or published this month; Scheduled = neither yet,
 * but scheduled to publish this month; No activity = none of the above.
 */
const OACAuditTab = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<OacChannelLastUpdateRow[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useRef<Toast>(null);

  const handleCopyChannelId = (channelId: string) => {
    navigator.clipboard.writeText(channelId).then(() => {
      toast.current?.show({
        severity: "success",
        summary: "Copied to clipboard",
        detail: "Channel ID copied",
        life: 2000,
      });
    });
  };

  const handleCreatePlaylist = (channelId: string) => {
    navigate(
      `${ROUTES.OAC_AUDIT_PLAYLISTS}?channelId=${encodeURIComponent(channelId)}`
    );
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiService.oacAuditChannelIds.getChannelLastUpdates();
        if (!cancelled) {
          setData(res.data);
          setUpdatedAt(res.updatedAt);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedData = useMemo(() => {
    return [...data].sort(
      (a, b) =>
        OAC_ACTIVITY_STATUS_ORDER[getOacActivityStatus(a)] -
        OAC_ACTIVITY_STATUS_ORDER[getOacActivityStatus(b)]
    );
  }, [data]);

  const renderStatus = (row: OacChannelLastUpdateRow) => {
    const status = getOacActivityStatus(row);
    const severity = oacActivityTagSeverity(status);
    return (
      <span style={{ whiteSpace: "nowrap" }}>
        <Tag severity={severity} value={OAC_ACTIVITY_STATUS_LABEL[status]} />
      </span>
    );
  };

  const renderChannelId = (row: OacChannelLastUpdateRow) => (
    <span
      role="button"
      tabIndex={0}
      onClick={() => handleCopyChannelId(row.channelId)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCopyChannelId(row.channelId);
        }
      }}
      className="cursor-pointer"
      title="Click to copy"
    >
      {row.channelId}
    </span>
  );

  const renderActions = (row: OacChannelLastUpdateRow) => (
    <Button
      label="Create playlist"
      icon="pi pi-list"
      size="small"
      text
      onClick={() => handleCreatePlaylist(row.channelId)}
      title="Open Create Playlist with this channel selected"
    />
  );

  return (
    <div>
      <Toast ref={toast} />
      <h3>OAC Audit</h3>
      {updatedAt && (
        <p className="text-color-secondary mb-3">
          Data as of {formatDate(updatedAt)}
        </p>
      )}
      {!loading && data.length > 0 && (
        <p className="text-color-secondary mb-3">
          Compliant: uploaded or published this month. Scheduled: nothing
          uploaded or published yet, but scheduled to publish this month. No
          activity: no upload, publish or schedule this month.
        </p>
      )}
      {loading ? (
        <p>Loading...</p>
      ) : data.length === 0 ? (
        <p>
          No channel last-update data for the selected audit channels. Run a
          sync or add channels in Channel Settings.
        </p>
      ) : (
        <DataTable
          value={sortedData}
          size="small"
          stripedRows
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
        >
          <Column
            field="channelId"
            header="Channel ID"
            body={(row: OacChannelLastUpdateRow) => renderChannelId(row)}
          />
          <Column field="channelDisplayName" header="Channel display name" />
          <Column
            header="Status"
            body={(row: OacChannelLastUpdateRow) => renderStatus(row)}
            bodyStyle={{ whiteSpace: "nowrap" }}
          />
          <Column
            field="lastPublishedAt"
            header="Last published at"
            body={(row: OacChannelLastUpdateRow) =>
              formatDate(row.lastPublishedAt)
            }
          />
          <Column
            field="lastScheduledAtAny"
            header="Last scheduled (any)"
            body={(row: OacChannelLastUpdateRow) =>
              formatDate(row.lastScheduledAtAny)
            }
          />
          <Column
            field="lastScheduledThisMonth"
            header="Last scheduled this month"
            body={(row: OacChannelLastUpdateRow) =>
              formatDate(row.lastScheduledThisMonth)
            }
          />
          <Column
            field="lastUploadAt"
            header="Last upload at"
            body={(row: OacChannelLastUpdateRow) =>
              formatDate(row.lastUploadAt)
            }
          />
          <Column
            header="Actions"
            body={(row: OacChannelLastUpdateRow) => renderActions(row)}
            bodyStyle={{ whiteSpace: "nowrap" }}
          />
        </DataTable>
      )}
    </div>
  );
};

export default OACAuditTab;
